import { Button } from '@/global/components/Button'
import { cn } from '@/lib/utils'
import { ChevronDownIcon, ChevronUpIcon } from 'lucide-react'
import { useEffect, useRef } from 'react'

const dois = (numero: number) => String(numero).padStart(2, '0')

const HORAS = Array.from({ length: 24 }, (_, hora) => dois(hora))
const MINUTOS = Array.from({ length: 60 }, (_, minuto) => dois(minuto))

/**
 * Altura de cada célula, em px. É constante — e não o que o conteúdo pedir —
 * porque toda a conta de rolagem (centralizar o escolhido, dar a volta no fim
 * da lista) é feita a partir dela.
 */
const ALTURA_ITEM = 40

/** Sete células: a janela é ímpar para haver um centro. */
const ALTURA_JANELA = ALTURA_ITEM * 7

/** Altura de cada chevron, em px — entra na conta do centro da janela. */
const ALTURA_CHEVRON = 28

/** Quantas cópias da lista existem para a volta ser imperceptível. */
const COPIAS = 3

/**
 * Corpo do número por distância até o meio da janela. Nenhum item é preenchido
 * nem ganha a cor primária: o que diz qual está escolhido é o tamanho, e é ele
 * que anima — a cor piscando a cada célula durante a rolagem cansa a vista.
 */
const CORPO_POR_DISTANCIA = [
    'text-2xl text-foreground',
    'text-lg text-foreground/75',
    'text-base text-muted-foreground',
    'text-sm text-muted-foreground/60',
]

export type TimePickerProps = {
    /** Hora selecionada em `HH:mm`. Vazio quando não há valor. */
    value?: string
    onChange: (value: string) => void
    className?: string
}

/**
 * Duas colunas roláveis — horas e minutos — para o popover do `DateField` no
 * modo data-hora. Fica ao lado do grid de dias: a data se escolhe à esquerda, a
 * hora à direita, sem fechar e reabrir nada.
 *
 * O gesto é o do `Select`: barra de rolagem escondida e chevrons nas pontas.
 * A lista dá a volta — depois de `23` vem `00` —, então nenhuma ponta é um beco
 * sem saída.
 */
export function TimePicker({ value, onChange, className }: TimePickerProps) {
    const [hora = '', minuto = ''] = value?.split(':') ?? []

    return (
        <div className={cn('flex border-l', className)}>
            <Coluna
                aria-label="Hora"
                itens={HORAS}
                selecionado={hora}
                onSelect={novaHora => onChange(`${novaHora}:${minuto || '00'}`)}
            />
            <Coluna
                aria-label="Minuto"
                itens={MINUTOS}
                selecionado={minuto}
                onSelect={novoMinuto =>
                    onChange(`${hora || '00'}:${novoMinuto}`)
                }
            />
        </div>
    )
}

type ColunaProps = {
    'aria-label': string
    itens: string[]
    selecionado: string
    onSelect: (item: string) => void
}

function Coluna({
    'aria-label': rotulo,
    itens,
    selecionado,
    onSelect,
}: ColunaProps) {
    const listaRef = useRef<HTMLDivElement>(null)
    /**
     * A centralização inicial é uma rolagem como outra qualquer para o
     * navegador, e sem esta trava ela escolheria um valor sozinha em um campo
     * que estava vazio.
     */
    const posicionandoRef = useRef(true)
    const alturaDaLista = itens.length * ALTURA_ITEM

    /** Rolagem que deixa o item de índice `indice` no meio da janela. */
    const centralizar = (indice: number) =>
        // A conta parte do índice, e não do `offsetTop` do elemento: dentro do
        // popover o `offsetParent` não é a coluna, e o `offsetTop` viria
        // deslocado da altura dos chevrons.
        alturaDaLista + indice * ALTURA_ITEM - (ALTURA_JANELA - ALTURA_ITEM) / 2

    /** Deixa o item escolhido no meio da janela; sem escolha, começa no `00`. */
    useEffect(() => {
        const lista = listaRef.current
        if (!lista) return
        posicionandoRef.current = true
        lista.scrollTop = centralizar(Math.max(itens.indexOf(selecionado), 0))
        const quadro = requestAnimationFrame(() => {
            posicionandoRef.current = false
        })
        return () => cancelAnimationFrame(quadro)
    }, [alturaDaLista])

    /**
     * A roda do mouse anda uma célula por entalhe, e não os pixels que o
     * sistema mandaria. É o `addEventListener` na mão porque o `onWheel` do
     * React é passivo — sem o `preventDefault` a rolagem nativa andaria junto,
     * e dentro de um diálogo ela nem chega a acontecer.
     */
    useEffect(() => {
        const lista = listaRef.current
        if (!lista) return
        const aoGirar = (evento: WheelEvent) => {
            evento.preventDefault()
            lista.scrollTop += Math.sign(evento.deltaY) * ALTURA_ITEM
        }
        lista.addEventListener('wheel', aoGirar, { passive: false })
        return () => lista.removeEventListener('wheel', aoGirar)
    }, [])

    /**
     * A volta da lista. As três cópias são iguais, então empurrar a rolagem uma
     * lista para cima ou para baixo repõe o mesmo desenho na tela — o usuário
     * vê a sequência continuar, e nunca chega a um fim.
     *
     * Rolar já escolhe: o valor do campo é o item que está no meio da janela,
     * sem precisar de clique.
     */
    const aoRolar = () => {
        const lista = listaRef.current
        if (!lista) return
        if (lista.scrollTop < alturaDaLista) lista.scrollTop += alturaDaLista
        else if (lista.scrollTop >= alturaDaLista * 2)
            lista.scrollTop -= alturaDaLista

        if (posicionandoRef.current) return

        const centro = lista.scrollTop + (ALTURA_JANELA - ALTURA_ITEM) / 2
        const noMeio = itens[Math.round(centro / ALTURA_ITEM) % itens.length]
        if (noMeio && noMeio !== selecionado) onSelect(noMeio)
    }

    /**
     * Distância circular até o item que está no meio — circular porque, na
     * emenda entre duas cópias, o vizinho de `00` é o `23`.
     */
    const indiceSelecionado = itens.indexOf(selecionado)
    const distanciaDoCentro = (indice: number) => {
        if (indiceSelecionado < 0) return CORPO_POR_DISTANCIA.length - 1
        const bruta = Math.abs(indice - indiceSelecionado)
        return Math.min(
            bruta,
            itens.length - bruta,
            CORPO_POR_DISTANCIA.length - 1,
        )
    }

    const passar = (direcao: 1 | -1) => {
        const lista = listaRef.current
        if (!lista) return
        lista.scrollTop += direcao * ALTURA_ITEM
    }

    return (
        <div className="relative flex w-16 flex-col">
            <Chevron direcao={-1} onPassar={passar} />
            <div
                ref={listaRef}
                onScroll={aoRolar}
                className={cn(
                    'overflow-y-auto overscroll-contain',
                    // Barra escondida, como no `Select`: quem rola são os
                    // chevrons, a roda do mouse e o arrasto.
                    '[scrollbar-width:none] [&::-webkit-scrollbar]:hidden',
                )}
                style={{ height: ALTURA_JANELA }}
                role="listbox"
                aria-label={rotulo}
                tabIndex={-1}
            >
                {Array.from({ length: COPIAS }, (_, copia) => (
                    <div
                        key={copia}
                        // Só a cópia do meio é a lista de verdade; as das
                        // pontas existem para a volta e não são anunciadas.
                        aria-hidden={copia !== 1 || undefined}
                    >
                        {itens.map((item, indice) => (
                            <Button
                                key={item}
                                type="button"
                                role="option"
                                aria-selected={item === selecionado}
                                tabIndex={copia === 1 ? undefined : -1}
                                variant="text"
                                color="primary"
                                className={cn(
                                    'w-full min-w-0 rounded-none px-0 py-0',
                                    'font-normal normal-case tracking-normal',
                                    // Só o corpo do número muda, e com
                                    // transição: enquanto a lista rola, cada
                                    // número cresce ao se aproximar do meio,
                                    // que é o que dá a sensação de roda.
                                    'transition-[font-size] duration-100 ease-out',
                                    CORPO_POR_DISTANCIA[
                                        distanciaDoCentro(indice)
                                    ],
                                )}
                                style={{ height: ALTURA_ITEM }}
                                onClick={() => onSelect(item)}
                            >
                                {item}
                            </Button>
                        ))}
                    </div>
                ))}
            </div>
            <Chevron direcao={1} onPassar={passar} />
            {/*
             * A célula do meio não rola: é ela que mostra o valor da coluna,
             * enquanto os números passam por baixo. `pointer-events-none` para
             * a roda do mouse e o arrasto continuarem chegando na lista que
             * está atrás — e `aria-hidden` porque quem anuncia a escolha é o
             * `listbox`.
             */}
            <Button
                type="button"
                variant="contained"
                color="primary"
                tabIndex={-1}
                aria-hidden
                className={cn(
                    'pointer-events-none absolute inset-x-0 rounded-none shadow-none',
                    'text-2xl font-normal normal-case tracking-normal',
                )}
                style={{
                    top: ALTURA_CHEVRON + (ALTURA_JANELA - ALTURA_ITEM) / 2,
                    height: ALTURA_ITEM,
                }}
            >
                {selecionado || '--'}
            </Button>
        </div>
    )
}

type ChevronProps = {
    direcao: 1 | -1
    onPassar: (direcao: 1 | -1) => void
}

/** Anda uma célula no clique e continua andando enquanto o botão fica preso. */
function Chevron({ direcao, onPassar }: ChevronProps) {
    const repeticaoRef = useRef<ReturnType<typeof setInterval>>(undefined)

    const parar = () => clearInterval(repeticaoRef.current)
    useEffect(() => parar, [])

    const Icone = direcao === -1 ? ChevronUpIcon : ChevronDownIcon

    return (
        <Button
            type="button"
            variant="text"
            color="inherit"
            tabIndex={-1}
            aria-hidden
            className="w-full min-w-0 shrink-0 rounded-none p-0 text-muted-foreground"
            style={{ height: ALTURA_CHEVRON }}
            onPointerDown={() => {
                onPassar(direcao)
                repeticaoRef.current = setInterval(() => onPassar(direcao), 100)
            }}
            onPointerUp={parar}
            onPointerLeave={parar}
        >
            <Icone className="size-4" />
        </Button>
    )
}
